import React, { useEffect, useState, useCallback } from 'react';
import { activityLogsAPI } from '../services/api';
import CancelClockInButton from '../components/CancelClockInButton';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import { formatDateTime } from '../utils/dateFormat';
import styles from '../styles/ActivitiesPage.module.css';

const PAGE_SIZE = 50;

const ActivitiesPage = () => {
  const { user } = useAuth();
  const { t } = useLocalization();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchLogs = useCallback(async (pageNum) => {
    try {
      setLoading(true);
      setError(null);
      const limit = pageNum * PAGE_SIZE;
      const response = await activityLogsAPI.getAll(limit + 1);
      const data = response.data || [];
      setHasMore(data.length > limit);
      setLogs(data.slice(0, limit));
    } catch {
      setError(t('activities_load_failed', 'Failed to load activities.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLogs(page);
  }, [page, fetchLogs]);

  const formatTime = (value) => formatDateTime(value);

  const handleClearLogs = async () => {
    const confirmed = window.confirm(t('activities_clear_confirm', 'Clear all activity logs? This action cannot be undone.'));
    if (!confirmed) return;

    try {
      setClearing(true);
      setError(null);
      await activityLogsAPI.clearAll();
      setPage(1);
      await fetchLogs(1);
    } catch {
      setError(t('activities_clear_failed', 'Failed to clear logs.'));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <i className="bi bi-journal-bookmark-fill" aria-hidden="true"></i> {t('activities_title', 'Activities')}
          </h1>
          <p className={styles.subtitle}>{t('activities_subtitle', 'Full audit log of all user actions')}</p>
        </div>
        {user?.role === 'admin' && (
          <button
            type="button"
            className={styles.clearBtn}
            disabled={clearing}
            onClick={handleClearLogs}
          >
            <i className="bi bi-trash3" aria-hidden="true"></i>
            <span>{clearing ? t('activities_clearing', 'Clearing...') : t('activities_clear_all', 'Clear All Logs')}</span>
          </button>
        )}
      </div>

      <div className={styles.tableWrap}>
        {error && <div className={styles.error}>{error}</div>}

        {!error && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('activities_description', 'Description')}</th>
                <th>{t('activities_user', 'User')}</th>
                <th>{t('activities_module', 'Module')}</th>
                <th>{t('activities_action', 'Action')}</th>
                <th>{t('activities_time', 'Time')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className={styles.empty}>{t('activities_no_data', 'No activities found.')}</td>
                </tr>
              ) : (
                logs.map((log, i) => (
                  <tr key={log.id}>
                    <td className={styles.numCell}>{i + 1}</td>
                    <td>
                      {log.description || `${log.actor_username} ${log.action_name} ${log.module_name}`}
                    </td>
                    <td>
                      <span className={`${styles.badge} ${styles[`badge_${log.actor_role}`]}`}>
                        {log.actor_username}
                      </span>
                    </td>
                    <td>{log.module_name}</td>
                    <td>
                      <span className={`${styles.action} ${styles[`action_${log.action_name}`]}`}>
                        {log.action_name}
                      </span>
                    </td>
                    <td className={styles.timeCell}>
                      {formatTime(log.created_at)}
                      {user && (user.role === 'admin' || user.role === 'supervisor') &&
                        log.module_name === 'availability' && log.action_name === 'clock_in' && log.target_user_id && log.target_date && (
                          <CancelClockInButton userId={log.target_user_id} date={log.target_date} onSuccess={() => fetchLogs(page)} />
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {loading && <div className={styles.loading}>{t('activities_loading', 'Loading...')}</div>}

        {!loading && !error && (
          <div className={styles.pagination}>
            {page > 1 && (
              <button className={styles.pageBtn} onClick={() => setPage((p) => p - 1)}>
                &larr; {t('activities_previous', 'Previous')}
              </button>
            )}
            <span className={styles.pageInfo}>{t('activities_page_prefix', 'Page')} {page}</span>
            {hasMore && (
              <button className={styles.pageBtn} onClick={() => setPage((p) => p + 1)}>
                {t('activities_next', 'Next')} &rarr;
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivitiesPage;
